// Kas & Bank — Dialog Input Transaksi Manual (2026-09-23).
// Digunakan di 3 sub-tab: Penerimaan / Pembayaran / Transfer Bank.
// Transfer = pilih rekening asal + tujuan → sistem catat 2 baris (keluar+masuk).
import { useEffect, useMemo, useState } from "react";
import { X, Save, Plus, Trash2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import { createManualEntry, updateManualEntry, deleteManualEntry, MANUAL_KIND_LABEL } from "@/lib/kasBankManual";
import { fetchCOA, buildProjectOptions } from "@/lib/kasBankClassification";
import SearchableSelect from "@/components/SearchableSelect";

/**
 * Props:
 * - kind: "penerimaan" | "pembayaran" | "transfer"
 * - entry?: existing manual entry (edit mode)
 * - onClose(), onSaved(data)
 * - canDelete?: bool
 */
export default function ManualEntryDialog({ kind, entry, onClose, onSaved, canDelete = true }) {
  const isEdit = !!entry;
  const isTransfer = kind === "transfer";
  const todayJKT = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
  const [form, setForm] = useState({
    date: entry?.date?.slice(0, 10) || todayJKT(),
    amount: entry?.amount || "",
    description: entry?.description || "",
    account_id: entry?.account_id || "",
    counter_account_id: entry?.counter_account_id || "",
    coa_id: entry?.coa_id || "",
    doc_no: entry?.doc_no || "",
    note: entry?.note || "",
    project_id: entry?.project_id || "",
  });
  const [accounts, setAccounts] = useState([]);
  const [coa, setCoa] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company-bank-accounts").then((r) => setAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => setAccounts([]));
    fetchCOA().then(setCoa).catch(() => setCoa([]));
    api.get("/active-projects").then((r) => setProjects(Array.isArray(r.data) ? r.data.map((p) => ({ id: p.id, project_name: p.project_name })) : [])).catch(() => setProjects([]));
  }, []);

  const setField = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const isValid = useMemo(() => {
    const amt = Number(form.amount);
    if (!form.date || !form.description.trim() || !form.account_id || !(amt > 0)) return false;
    if (isTransfer) {
      if (!form.counter_account_id) return false;
      if (form.counter_account_id === form.account_id) return false;
    }
    return true;
  }, [form, isTransfer]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      setSaving(true);
      const pjName = projects.find((p) => p.id === form.project_id)?.project_name || null;
      const payload = {
        date: form.date,
        amount: Number(form.amount),
        description: form.description.trim(),
        account_id: form.account_id,
        coa_id: form.coa_id || null,
        doc_no: form.doc_no || null,
        note: form.note || null,
        project_id: form.project_id || null,
        project_name: pjName,
      };
      let saved;
      if (isEdit) {
        // Edit: kind & account tidak boleh berubah (menghindari saldo mismatch).
        const { account_id: _a, ...patch } = payload;  // eslint-disable-line no-unused-vars
        saved = await updateManualEntry(entry.id, patch);
      } else {
        payload.kind = kind;
        if (isTransfer) payload.counter_account_id = form.counter_account_id;
        saved = await createManualEntry(payload);
      }
      toast.success(isEdit ? "Transaksi diperbarui." : (isTransfer ? "Transfer bank tercatat (2 baris)." : "Transaksi tercatat."));
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan transaksi.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!entry) return;
    const isTr = entry.kind === "transfer_out" || entry.kind === "transfer_in";
    if (!window.confirm(isTr ? "Hapus transfer bank? Kedua sisi (masuk + keluar) akan ikut terhapus." : "Hapus transaksi manual ini?")) return;
    try {
      await deleteManualEntry(entry.id);
      toast.success("Transaksi dihapus.");
      onSaved?.(null);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus transaksi.");
    }
  };

  const kindLabel = MANUAL_KIND_LABEL[kind] || "Transaksi";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="kbm-dialog">
      <form onSubmit={submit} className="bg-white w-full max-w-2xl border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {isTransfer ? <ArrowLeftRight size={16} className="text-[#FF5A1F]" /> : <Plus size={16} className="text-[#FF5A1F]" />}
            <div>
              <div className="overline text-[10px] text-slate-500 tracking-widest">INPUT TRANSAKSI MANUAL</div>
              <div className="font-display text-base font-semibold text-[#0B1D3A]">{isEdit ? `Ubah ${kindLabel}` : `Catat ${kindLabel}`}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="kbm-close"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3 text-[11px]">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Tanggal *</div>
              <input type="date" value={form.date} onChange={setField("date")} data-testid="kbm-date"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Nominal (IDR) *</div>
              <input type="number" min="1" step="0.01" value={form.amount} onChange={setField("amount")} data-testid="kbm-amount"
                placeholder="0" className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white tabular-nums focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
              {Number(form.amount) > 0 && (
                <div className="text-[10px] tabular-nums text-slate-500 mt-0.5">{formatIDR(Number(form.amount))}</div>
              )}
            </div>
          </div>

          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Deskripsi *</div>
            <input value={form.description} onChange={setField("description")} data-testid="kbm-desc" maxLength={200}
              placeholder="Uraian singkat mutasi" className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
          </div>

          <div className={`grid gap-2 ${isTransfer ? "grid-cols-2" : "grid-cols-1"}`}>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">{isTransfer ? "Rekening Asal *" : "Rekening *"}</div>
              <SearchableSelect value={form.account_id} onChange={setField("account_id")} disabled={isEdit}
                options={accounts.map((a) => ({ value: a.id, label: `${a.bank_name} · ${a.account_no}` }))}
                placeholder="— Pilih rekening —" testid="kbm-account" />
              {isEdit && <div className="text-[10px] text-slate-400 italic mt-0.5">Rekening tidak bisa diubah setelah entri disimpan.</div>}
            </div>
            {isTransfer && (
              <div>
                <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Rekening Tujuan *</div>
                <SearchableSelect value={form.counter_account_id} onChange={setField("counter_account_id")} disabled={isEdit}
                  options={accounts.filter((a) => a.id !== form.account_id).map((a) => ({ value: a.id, label: `${a.bank_name} · ${a.account_no}` }))}
                  placeholder="— Pilih rekening tujuan —" testid="kbm-counter-account" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Kategori Akun (COA)</div>
              <SearchableSelect value={form.coa_id} onChange={setField("coa_id")}
                options={[{ value: "", label: "— Tanpa Kategori —" }, ...coa.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))]}
                placeholder="— Tanpa Kategori —" testid="kbm-coa" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Nomor Dokumen</div>
              <input value={form.doc_no} onChange={setField("doc_no")} data-testid="kbm-docno" maxLength={64}
                placeholder="Manual, opsional" className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Proyek Terkait (opsional)</div>
              <SearchableSelect value={form.project_id} onChange={setField("project_id")}
                options={buildProjectOptions(projects)}
                placeholder="— Tanpa Proyek —" testid="kbm-project" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Catatan</div>
              <input value={form.note} onChange={setField("note")} data-testid="kbm-note" maxLength={200}
                placeholder="Catatan bebas" className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
          </div>

          {isTransfer && !isEdit && (
            <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 p-2 italic">
              Transfer akan tercatat sebagai <b>2 baris</b> di Riwayat Bank — satu keluar di rekening asal + satu masuk di rekening tujuan, saling terhubung (hapus salah satu akan menghapus keduanya).
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <div>
            {isEdit && canDelete && (
              <button type="button" onClick={remove} data-testid="kbm-delete"
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-wider font-semibold">
                <Trash2 size={12} /> Hapus Transaksi
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} data-testid="kbm-cancel"
              className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Batal</button>
            <button type="submit" disabled={saving || !isValid} data-testid="kbm-save"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
              <Save size={12} /> {saving ? "Menyimpan…" : (isEdit ? "Simpan Perubahan" : "Simpan")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
